/**
 * ImportProgressIndicator - Reusable component for showing import progress
 * Used in both CampaignDetailPage and ContactsViewerModal
 */
import { Box, Typography, LinearProgress, Chip, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { 
  Refresh, 
  CloudDownload, 
  Description, 
  Settings, 
  Save, 
  AutoAwesome 
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import { useNumberFormat } from '../hooks/useNumberFormat';

export interface ImportProgressData {
  progress: number;
  processedRows: number;
  totalRows: number;
  message?: string;
  estimatedRemainingSeconds?: number;
  phase?: string;
}

interface ImportProgressIndicatorProps {
  importProgress: ImportProgressData;
  fullWidth?: boolean;
}

export default function ImportProgressIndicator({ 
  importProgress,
  fullWidth = false,
}: ImportProgressIndicatorProps) {
  const { t } = useTranslation(['pages', 'common']);
  const { formatDuration, formatNumber, formatPercent } = useNumberFormat();
  const phases = ['downloading', 'parsing', 'validating', 'processing', 'saving', 'finalizing'] as const;
  const phaseLabels: Record<string, string> = {
    downloading: t('pages:campaigns.detail.contactsFile.importProgress.phaseLabels.downloading'),
    parsing: t('pages:campaigns.detail.contactsFile.importProgress.phaseLabels.parsing'),
    validating: t('pages:campaigns.detail.contactsFile.importProgress.phaseLabels.validating'),
    processing: t('pages:campaigns.detail.contactsFile.importProgress.phaseLabels.processing'),
    saving: t('pages:campaigns.detail.contactsFile.importProgress.phaseLabels.saving'),
    finalizing: t('pages:campaigns.detail.contactsFile.importProgress.phaseLabels.finalizing'),
  };
  const phaseIcons: Record<string, SvgIconComponent> = {
    downloading: CloudDownload,
    parsing: Description,
    validating: Settings,
    processing: Settings,
    saving: Save,
    finalizing: AutoAwesome,
  };

  const normalizedPhase = phases.includes((importProgress.phase as typeof phases[number]) || 'processing')
    ? ((importProgress.phase as typeof phases[number]) || 'processing')
    : 'processing';
  const currentPhaseIndex = phases.indexOf(normalizedPhase);

  const getPhaseTitle = () => {
    switch (importProgress.phase) {
      case 'downloading':
        return t('pages:campaigns.detail.contactsFile.importProgress.phaseTitles.downloading');
      case 'parsing':
        return t('pages:campaigns.detail.contactsFile.importProgress.phaseTitles.parsing');
      case 'validating':
        return t('pages:campaigns.detail.contactsFile.importProgress.phaseTitles.validating');
      case 'processing':
        return t('pages:campaigns.detail.contactsFile.importProgress.phaseTitles.processing');
      case 'saving':
        return t('pages:campaigns.detail.contactsFile.importProgress.phaseTitles.saving');
      case 'finalizing':
        return t('pages:campaigns.detail.contactsFile.importProgress.phaseTitles.finalizing');
      default:
        return t('pages:campaigns.detail.contactsFile.importProgress.phaseTitles.default');
    }
  };

  const getPhaseDescription = () => {
    switch (importProgress.phase) {
      case 'downloading':
        return t('pages:campaigns.detail.contactsFile.importProgress.phaseDescriptions.downloading');
      case 'parsing':
        return t('pages:campaigns.detail.contactsFile.importProgress.phaseDescriptions.parsing');
      case 'validating':
        return t('pages:campaigns.detail.contactsFile.importProgress.phaseDescriptions.validating', {
          totalRows: formatNumber(importProgress.totalRows),
        });
      case 'processing':
        return t('pages:campaigns.detail.contactsFile.importProgress.phaseDescriptions.processing', {
          processedRows: formatNumber(importProgress.processedRows),
          totalRows: formatNumber(importProgress.totalRows),
        });
      case 'saving':
        return t('pages:campaigns.detail.contactsFile.importProgress.phaseDescriptions.saving');
      case 'finalizing':
        return t('pages:campaigns.detail.contactsFile.importProgress.phaseDescriptions.finalizing');
      default:
        return t('pages:campaigns.detail.contactsFile.importProgress.phaseDescriptions.default');
    }
  };

  const PhaseIcon = phaseIcons[importProgress.phase || 'processing'] || Settings;

  return (
    <Alert 
      severity="info" 
      sx={{ 
        mb: 2,
        width: '100%',
        ...(fullWidth && {
          mx: 0,
          borderRadius: 0,
          '& .MuiAlert-message': {
            width: '100%',
          },
        }),
      }} 
      icon={<Refresh className="spin" />}
    >
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhaseIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2" fontWeight={500}>
              {getPhaseTitle()}
            </Typography>
          </Box>
          <Typography variant="body2" fontWeight={500} color="primary">
            {formatPercent(importProgress.progress)}
          </Typography>
        </Box>
        <LinearProgress 
          variant={importProgress.phase === 'downloading' || importProgress.phase === 'parsing' ? 'indeterminate' : 'determinate'}
          value={importProgress.progress} 
          sx={{ mt: 0.5, mb: 1, height: 8, borderRadius: 1 }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {getPhaseDescription()}
          </Typography>
          {importProgress.phase === 'processing' && importProgress.totalRows > 0 && (
            <Typography variant="caption" color="text.secondary">
              {formatNumber(importProgress.processedRows)} / {formatNumber(importProgress.totalRows)} {t('common:common.rows')}
            </Typography>
          )}
        </Box>
        {importProgress.phase === 'processing'
          && importProgress.estimatedRemainingSeconds != null
          && importProgress.estimatedRemainingSeconds > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t('pages:campaigns.detail.contactsFile.importProgress.estimatedRemaining', {
                duration: formatDuration(importProgress.estimatedRemainingSeconds),
              })}
            </Typography>
          )}
        {/* Phase indicators */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          {phases.map((phase, index) => {
            const isCompleted = index < currentPhaseIndex;
            const isCurrent = index === currentPhaseIndex;
            
            return (
              <Chip
                key={phase}
                label={phaseLabels[phase]}
                size="small"
                color={isCurrent ? 'primary' : isCompleted ? 'success' : 'default'}
                variant={isCurrent ? 'filled' : 'outlined'}
                sx={{ 
                  fontSize: '0.7rem', 
                  height: 22,
                  opacity: isCompleted || isCurrent ? 1 : 0.5,
                }}
              />
            );
          })}
        </Box>
      </Box>
    </Alert>
  );
}
